import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ChecklistsPadraoResultado, Esfera, Obrigacao, RegimeTributario } from '@sgc/contracts';
import { get, post } from '../lib/api';
import { useToast } from '../ui/toast';
import { OPCOES_REGIME, ROTULO_PERIODICIDADE } from '../lib/formato';
import { Campo, Sel } from '../ui/campos';
import { Pill, Sec } from '../ui/layout';
import { useAuth } from '../lib/auth';
import { ModalNovaObrigacao } from '../telas/obrigacoes/nova';

export const Route = createFileRoute('/_app/obrigacoes/')({ component: Obrigacoes });

const ESFERAS: ReadonlyArray<readonly [Esfera, string]> = [['FEDERAL', 'Federal'], ['ESTADUAL', 'Estadual'], ['MUNICIPAL', 'Municipal'], ['INTERNA', 'Interna']];

function Obrigacoes() {
  const [busca, setBusca] = useState('');
  const [esfera, setEsfera] = useState<Esfera | ''>('');
  const [regime, setRegime] = useState<RegimeTributario | ''>('');
  const [nova, setNova] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();
  const copiarTodas = useMutation({ mutationFn: () => post<{ copiadas: number; jaExistiam: number }>('/v1/obrigacoes/copiar-todas'), onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ['obrigacoes'] }); toast.ok(r.copiadas ? `${r.copiadas} obrigação(ões) copiada(s) para o escritório — agora todas são editáveis` : 'todas já estavam copiadas'); }, onError: (e) => toast.erro(e) });
  const checklists = useMutation({
    mutationFn: () => post<ChecklistsPadraoResultado>('/v1/obrigacoes/checklists/padrao'),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ['obrigacoes'] }); toast.ok(r.preenchidas ? `${r.preenchidas} obrigação(ões) ganharam checklist${r.semPadrao ? ` · ${r.semPadrao} criada(s) pelo escritório seguem sem (monte o seu na ficha da obrigação)` : ''}` : 'todas as obrigações do catálogo já têm checklist'); },
    onError: (e) => toast.erro(e),
  });
  const papel = useAuth().eu?.escritorio?.papel;
  const supervisor = papel === 'ADMIN' || papel === 'SUPERVISOR';
  const q = useQuery({ queryKey: ['obrigacoes', esfera, regime], queryFn: () => get<Obrigacao[]>('/v1/obrigacoes', { esfera: esfera || undefined, regime: regime || undefined }) });
  const globaisRestantes = (q.data ?? []).filter((o) => !o.escritorioId).length;
  const semChecklist = (q.data ?? []).filter((o) => !o.checklistItens && o.temChecklistPadrao).length; // só o que o botão consegue preencher
  const itens = (q.data ?? []).filter((o) => !busca || `${o.sigla} ${o.nome} ${o.codigo}`.toLowerCase().includes(busca.toLowerCase()));

  return (
    <>
      <div className="topbar">
        <div><h1>Obrigações</h1><div className="sub">catálogo com regra de prazo — confira cada uma no simulador antes de vincular clientes</div></div>
        <div className="acoes">
          <Link className="btn" to="/obrigacoes/simular">Simulador</Link>
          <Link className="btn" to="/feriados">Feriados</Link>
          {supervisor && globaisRestantes > 0 && <button className="btn" type="button" disabled={copiarTodas.isPending} title="cria uma cópia editável de cada obrigação do catálogo; os clientes vinculados migram para a cópia" onClick={() => { if (confirm(`Copiar as ${globaisRestantes} obrigações do catálogo para o escritório? Cada uma vira editável (regras, versões, checklist) e os clientes já vinculados passam a seguir a cópia. O catálogo original continua como referência.`)) copiarTodas.mutate(); }}>{copiarTodas.isPending ? 'copiando…' : `Copiar todas para o escritório (${globaisRestantes})`}</button>}
          {supervisor && semChecklist > 0 && <button className="btn" type="button" disabled={checklists.isPending} title="preenche o checklist sugerido nas obrigações que ainda não têm nenhum item; quem já tem fica como está" onClick={() => checklists.mutate()}>{checklists.isPending ? 'criando…' : `Criar checklists padrão (${semChecklist} sem)`}</button>}
          {supervisor && <button className="btn primario" type="button" onClick={() => setNova(true)}>+ Nova obrigação</button>}
        </div>
      </div>
      {nova && <ModalNovaObrigacao onFechar={() => setNova(false)} />}
      <div className="conteudo">
        <Sec titulo="Filtros">
          <div className="fgrid">
            <Campo className="c5" rotulo="Buscar" valor={busca} onChange={setBusca} placeholder="sigla, nome ou código" />
            <Sel className="c3" rotulo="Esfera" valor={esfera} vazio="todas" opcoes={ESFERAS} onChange={(v) => setEsfera(v as Esfera | '')} />
            <Sel className="c4" rotulo="Aplicável ao regime" valor={regime} vazio="todos" opcoes={OPCOES_REGIME} onChange={(v) => setRegime(v as RegimeTributario | '')} />
          </div>
        </Sec>
        <div className="tbwrap">
          <table className="tb">
            <thead><tr><th>Sigla</th><th>Nome</th><th>Esfera</th><th>Depto.</th><th>Periodicidade</th><th>Regra vigente</th><th>Regimes</th><th className="c">Checklist</th><th>Origem</th></tr></thead>
            <tbody>
              {q.isLoading && <tr><td className="vazio" colSpan={9}>carregando…</td></tr>}
              {itens.map((o) => (
                <tr key={o.id}>
                  <td><Link to="/obrigacoes/$id" params={{ id: o.id }}><b>{o.sigla}</b></Link></td>
                  <td>{o.nome}{!o.ativo && <> <Pill cor="verm">inativa</Pill></>}</td>
                  <td>{ESFERAS.find(([v]) => v === o.esfera)?.[1]}</td>
                  <td className="mono">{o.departamentoSugerido ?? ''}</td>
                  <td>{o.periodicidade ? ROTULO_PERIODICIDADE[o.periodicidade] : <span className="dim">—</span>}</td>
                  <td className="dim">{o.resumoRegra ?? '—'}</td>
                  <td className="dim">{o.regimesAplicaveis.length ? o.regimesAplicaveis.join(', ') : 'todos'}</td>
                  <td className="c">{o.checklistItens ? <span title="itens no checklist padrão">{o.checklistItens}</span> : <span className="dim" title="sem checklist: a tarefa nasce sem passos">—</span>}</td>
                  <td>{o.escritorioId ? <Pill cor="acento">do escritório</Pill> : <Pill>catálogo</Pill>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
